import { useState, useEffect, useCallback } from 'react';

const useSession = () => {
  const [sessionToken, setSessionToken] = useState(() => {
    // Inicializar desde localStorage si existe
    const token = localStorage.getItem('session_token');
    return token || null;
  });
  const [isValidating, setIsValidating] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [lastActivity, setLastActivity] = useState(() => {
    return localStorage.getItem('last_activity') ? parseInt(localStorage.getItem('last_activity')) : Date.now();
  });

  // Verificar validez de sesión
  const validateSession = useCallback(async () => {
    if (!sessionToken) return false;

    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/auth/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': sessionToken,
          'X-company-ID': localStorage.getItem('idcompany')
        },
        body: JSON.stringify({ session_token: sessionToken })
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      return data.valid === true;
    } catch (error) {
      console.error('Error validating session:', error);
      return false;
    }
  }, [sessionToken]);

  // Configurar sesión después del login
  const setSession = useCallback((token, userData) => {
    console.log('setSession called with:', { token, userData });
    const now = Date.now();
    setSessionToken(token);
    setLastActivity(now);
    localStorage.setItem('session_token', token);
    localStorage.setItem('last_activity', now.toString());
    
    // Guardar datos del usuario
    if (userData) {
      localStorage.setItem('id', userData.id);
      localStorage.setItem('nombre', userData.nombre);
      localStorage.setItem('usuario', userData.usuario);
      localStorage.setItem('idcompany', userData.idcompany);
      localStorage.setItem('nombre_compania', userData.nombre_compania);
      localStorage.setItem('id_sucursal', userData.id_sucursal);
      localStorage.setItem('nombre_sucursal', userData.nombre_sucursal);
      localStorage.setItem('id_almacen', userData.id_almacen);
      localStorage.setItem('nombre_almacen', userData.nombre_almacen);
      localStorage.setItem('permisos', JSON.stringify(userData.permisos));
      localStorage.setItem("modulos", JSON.stringify(userData.modulos));
      localStorage.setItem('isLoggedIn', true);
    }
  }, []);

  // Limpiar sesión
  const clearSession = useCallback(async () => {
    // Enviar logout al servidor si hay token
    if (sessionToken) {
      try {
        await fetch(`${process.env.REACT_APP_SERVER_URL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-company-ID': localStorage.getItem('idcompany'), 
          },
          body: JSON.stringify({ session_token: sessionToken })
        });
      } catch (error) {
        console.error('Error during logout:', error);
      }
    }

    // Limpiar datos locales
    setSessionToken(null);
    setLastActivity(Date.now());
    localStorage.removeItem('session_token');
    localStorage.removeItem('last_activity');
    localStorage.removeItem('id');
    localStorage.removeItem('nombre');
    localStorage.removeItem('usuario');
    localStorage.removeItem('idcompany');
    localStorage.removeItem('nombre_compania');
    localStorage.removeItem('id_sucursal');
    localStorage.removeItem('nombre_sucursal');
    localStorage.removeItem('id_almacen');
    localStorage.removeItem('nombre_almacen');
    localStorage.removeItem('permisos');
    localStorage.removeItem('modulos');
    localStorage.removeItem('isLoggedIn');
  }, [sessionToken]);

  // Actualizar actividad del usuario
  const updateActivity = useCallback(() => {
    const now = Date.now();
    setLastActivity(now);
    localStorage.setItem('last_activity', now.toString());
  }, []);

  // Verificar timeout de inactividad (2 horas)
  const checkInactivityTimeout = useCallback(() => {
    if (!sessionToken) return false;
    
    const now = Date.now();
    const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 10 minutos en milisegundos
    const timeSinceActivity = now - lastActivity;
    const timeRemaining = INACTIVITY_TIMEOUT - timeSinceActivity;
    
    
    if (now - lastActivity > INACTIVITY_TIMEOUT) {
      console.log('Session expired due to inactivity');
      clearSession();
      return true;
    }
    
    return false;
  }, [sessionToken, lastActivity, clearSession]);

  // Verificar timeout de inactividad cada 5 minutos
  useEffect(() => {
    if (!sessionToken) return;

    const interval = setInterval(() => {
      checkInactivityTimeout();
    }, 300000); // Verificar cada 5 minutos

    return () => clearInterval(interval);
  }, [sessionToken, checkInactivityTimeout]);

  // Marcar como inicializado después de la primera carga
  useEffect(() => {
    setIsInitialized(true);
  }, []);

  // Actualizar actividad cuando el usuario interactúa con la página
  useEffect(() => {
    const handleUserActivity = () => {
      updateActivity();
    };

    // Eventos de actividad del usuario
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, handleUserActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity);
      });
    };
  }, [updateActivity]);

  // Verificar sesión al cargar la página (solo si hay token)
  useEffect(() => {
    if (sessionToken && isInitialized) {
      const checkInitialSession = async () => {
        // Esperar un poco más para que el backend esté completamente listo
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        setIsValidating(true);
        try {
          const isValid = await validateSession();
          if (!isValid) {
            console.log('Session validation failed, clearing session');
            clearSession();
          } else {
            console.log('Session validation successful');
          }
        } catch (error) {
          console.error('Error during initial session validation:', error);
          // No limpiar sesión por errores de red, solo por sesiones inválidas
          console.log('Network error during validation, keeping session');
        }
        setIsValidating(false);
      };
      
      checkInitialSession();
    }
  }, [sessionToken, isInitialized, validateSession, clearSession]);

  return {
    sessionToken,
    isValidating,
    isInitialized,
    setSession,
    clearSession,
    validateSession,
    updateActivity
  };
};

export default useSession;
